import type { HITLConfig } from '../../tool-config-types';

const omitPreCreateCvssFields = (
  value: Record<string, unknown>,
): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries(value).filter(
      ([key]) =>
        key !== 'cvssVector' &&
        key !== 'cvssScore' &&
        key !== 'cvssExplanation',
    ),
  );

const toFindingRecord = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return null;
  }

  return Object.fromEntries(Object.entries(value));
};

/**
 * HITL config for finding-tool (from co-pilot)
 * Passes structured fields directly in args.
 * POC is optional - when traceThreadId is provided, POC is extracted from the trace.
 */
export const findingToolHitlConfig: HITLConfig = {
  extractData: (args: Record<string, unknown>): unknown[] => {
    const hasCoreFindingFields =
      (args.title || args.shortDescription) && args.description;

    if (hasCoreFindingFields) {
      return [args];
    }

    return [];
  },
  wrapEditedData: (data: unknown[]): Record<string, unknown> => {
    const finding = toFindingRecord(data[0]);

    return finding ? omitPreCreateCvssFields(finding) : {};
  },
};

/**
 * HITL config for structured findings actions (from other agents)
 * Handles findings arrays or single finding objects
 */
export const findingsActionHitlConfig: HITLConfig = {
  extractData: (args: Record<string, unknown>): unknown[] => {
    if (Array.isArray(args.findings)) {
      return args.findings;
    }

    if (args.finding && typeof args.finding === 'object') {
      return [args.finding];
    }

    if (args.title || args.severity || args.description) {
      return [args];
    }

    return [];
  },
  wrapEditedData: (data: unknown[]): Record<string, unknown> => ({
    findings: data.map((item) => {
      const finding = toFindingRecord(item);

      return finding ? omitPreCreateCvssFields(finding) : item;
    }),
  }),
};
